import createClient, { type Middleware } from "openapi-fetch";
import { t } from "@/shared/lib/i18n";
import { isNative, nativeFetch } from "@/shared/platform/native";
import type { paths } from "./generated/schema";

/**
 * The one place that talks to the server.
 *
 * Types come from the backend's OpenAPI document, so a route that changes shape
 * breaks the build rather than a screen. Nothing else in the application calls
 * `fetch`, which is what keeps authentication, CSRF and error handling from
 * being re-implemented slightly differently in a dozen components.
 *
 * Credentials live in an HttpOnly cookie the page cannot read. That is the
 * point: an XSS bug in this bundle cannot exfiltrate a long-lived token,
 * because there is no token here to take. The price is CSRF, paid by echoing a
 * readable token in a header — something a cross-origin page cannot do.
 */

/**
 * Paths carry their own `/api/v4` prefix, exactly as the OpenAPI document
 * writes them, so a route string in this codebase can be pasted into the docs
 * page and found. The base is therefore the origin, and the API is same-origin
 * in development too (Vite proxies it) — which means cookies, CSRF and relative
 * URLs behave identically in development and behind the production proxy.
 */
export const API_BASE = "";
const CSRF_PATH = "/api/v4/auth/csrf";

const ERROR_MESSAGES: Record<string, string> = {
  get bad_request() {
    return t("copy.7a7a80bfab44");
  },
  get unauthorized() {
    return t("copy.39f6162d8263");
  },
  get forbidden() {
    return t("copy.a25c909874f9");
  },
  get csrf_failed() {
    return t("copy.31377a9a1089");
  },
  get insufficient_scope() {
    return t("copy.4c8f6d456015");
  },
  get not_found() {
    return t("copy.c7c2d58c091b");
  },
  get conflict() {
    return t("copy.e53e2fae9cfc");
  },
  get validation_error() {
    return t("copy.560418b7fa5d");
  },
  get field_validation_failed() {
    return t("copy.7f98f92e9117");
  },
  get field_key_immutable() {
    return t("copy.232da33bbc54");
  },
  get field_type_incompatible() {
    return t("copy.70495730f4bf");
  },
  get rate_limited() {
    return t("copy.f2db1cf1a3a3");
  },
  get internal_error() {
    return t("copy.a240b9844254");
  },
  get invalid_credentials() {
    return t("copy.3ae448d5fd3f");
  },
  get registration_closed() {
    return t("copy.63aa560bc872");
  },
  get invalid_invite_code() {
    return t("copy.f531becf149c");
  },
  get email_already_registered() {
    return t("copy.ad844c2eddce");
  },
  get weak_password() {
    return t("copy.fed49c4fa912");
  },
  get invalid_password() {
    return t("copy.e9052867369c");
  },
  get confirmation_mismatch() {
    return t("copy.6c963742257c");
  },
  get invalid_reset_token() {
    return t("copy.bf647903640b");
  },
  get totp_already_set() {
    return t("copy.29264ede9afa");
  },
  get invalid_totp() {
    return t("copy.69b436bca334");
  },
  get invalid_totp_challenge() {
    return t("copy.fa96a514cee4");
  },
  get totp_challenge_expired() {
    return t("copy.bf96bcfec84f");
  },
  get totp_attempts_exceeded() {
    return t("copy.849189b4fc23");
  },
  get recent_auth_required() {
    return t("copy.b6b5914072ea");
  },
  get recent_mfa_required() {
    return t("copy.889b2f0761db");
  },
  get revision_mismatch() {
    return t("copy.006357b61a98");
  },
  get idempotency_key_reused() {
    return t("copy.4d9c0ce92710");
  },
  get idempotency_in_progress() {
    return t("copy.3a3c12e12293");
  },
  get unknown_ids() {
    return t("copy.a99421654cb2");
  },
  get project_not_found() {
    return t("copy.31bc0356a1f2");
  },
  get project_date_order() {
    return t("copy.6dbd7caa28b9");
  },
  get routine_date_order() {
    return t("copy.55101a434922");
  },
  get range_invalid() {
    return t("copy.eb3c89088cba");
  },
  get range_too_large() {
    return t("copy.c9f48253f21e");
  },
  get focus_already_running() {
    return t("copy.d3da6ac68787");
  },
  get focus_already_finished() {
    return t("copy.b2451eb290f2");
  },
  get draft_already_converted() {
    return t("copy.9f28c77266f0");
  },
  get file_rejected() {
    return t("copy.f10000000001");
  },
  get unsafe_file_type() {
    return t("copy.f10000000002");
  },
  get file_type_mismatch() {
    return t("copy.f10000000003");
  },
  get unsafe_archive() {
    return t("copy.f10000000004");
  },
  get malware_detected() {
    return t("copy.f10000000005");
  },
  get scanner_unavailable() {
    return t("copy.f10000000006");
  },
  get file_too_large() {
    return t("copy.f10000000007");
  },
  get file_quota_exceeded() {
    return t("copy.f10000000008");
  },
  get file_handle_already_attached() {
    return t("copy.f10000000009");
  },
  get file_no_longer_available() {
    return t("copy.f1000000000a");
  },
  get upload_expired() {
    return t("copy.f1000000000b");
  },
  get upload_in_progress() {
    return t("copy.f1000000000c");
  },
  get upload_not_ready() {
    return t("copy.f1000000000d");
  },
  get invalid_download_grant() {
    return t("copy.f1000000000e");
  },
  get blob_commit_failed() {
    return t("copy.f1000000000f");
  },
  get blob_integrity_failed() {
    return t("copy.f10000000010");
  },
  get blob_backend_unavailable() {
    return t("copy.f10000000011");
  },
  get file_reference_drift() {
    return t("copy.f10000000012");
  },
  get sync_reset_required() {
    return t("copy.24b3417c7991");
  },
  get schema_version_mismatch() {
    return t("copy.1bbfacaf52ac");
  },
  get schema_contract_conflict() {
    return t("copy.dfd828ce9648");
  },
  get schema_draft_not_found() {
    return t("copy.d7c79850987c");
  },
  get relation_rule_violation() {
    return t("copy.d392ee8d6c5c");
  },
  get relation_cardinality_exceeded() {
    return t("copy.9e38325184e6");
  },
  get relation_duplicate() {
    return t("copy.2024ae73a2bc");
  },
  get batch_limit_exceeded() {
    return t("copy.0f9c0f87258d");
  },
  get api_version_retired() {
    return t("copy.206c75ac5e79");
  },
  get unknown_backup_format() {
    return t("copy.a66f1848dfd8");
  },
  get malformed_backup() {
    return t("copy.30fe16bc36fb");
  },
  get backup_too_large() {
    return t("copy.6ddace0f7eca");
  },
  get restore_not_confirmed() {
    return t("copy.72a99d31ccfc");
  },
  get backup_integrity_failed() {
    return t("copy.f10000000013");
  },
  get backup_signature_invalid() {
    return t("copy.f10000000014");
  },
  get backup_signing_unavailable() {
    return t("copy.f10000000015");
  },
  get push_not_configured() {
    return t("copy.a17c324d6700");
  },
  get period_boundary_order() {
    return t("copy.29d574450a54");
  },
  get rationale_subject_not_found() {
    return t("copy.e5fe4bc69ee9");
  },
  get replan_session_closed() {
    return t("copy.40fdeba4f745");
  },
  get deviation_already_resolved() {
    return t("copy.4b0fee1a08c2");
  },
  get protected_window_blocked() {
    return t("copy.8ec85d81583e");
  },
};

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly requestId: string | undefined;
  private readonly originalFields: Record<string, string[]> | undefined;

  constructor(
    status: number,
    code: string,
    detail: string,
    fields?: Record<string, string[]>,
    requestId?: string | null,
  ) {
    super(detail);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.originalFields = fields;
    this.requestId = safeRequestId(requestId);
  }

  /** Display translated field errors; the original response remains stored internally. */
  get fields(): Record<string, string[]> | undefined {
    return translateFieldErrors(this.originalFields);
  }

  /** The session is gone or was never there. */
  get isUnauthorized() {
    return this.status === 401;
  }

  /** The request was refused because of what the data already says. */
  get isConflict() {
    return this.status === 409;
  }

  get isValidation() {
    return this.status === 422;
  }

  /** A message worth showing a person, as opposed to one worth logging. */
  get userMessage(): string {
    if (
      isNative() &&
      this.status === 403 &&
      this.message ===
        "This surface is only available to a first-party session or an unrestricted token."
    ) {
      return t("native.first_party_only");
    }
    if (this.status === 0) return t("copy.83e3c078ed2a");
    const translated = Object.hasOwn(ERROR_MESSAGES, this.code)
      ? ERROR_MESSAGES[this.code]
      : undefined;
    if (translated) return appendSupportReference(translated, this.requestId, this.status);
    const guidance =
      this.status >= 500
        ? t("copy.b4c203ba1d0a")
        : this.status === 401
          ? t("copy.39f6162d8263")
          : this.status === 403
            ? t("copy.a25c909874f9")
            : this.status === 404
              ? t("copy.7fad5c1a42c1")
              : this.status === 409
                ? t("copy.f30ce6821950")
                : this.status === 422
                  ? t("copy.74f1eed84c64")
                  : this.status === 429
                    ? t("copy.f2db1cf1a3a3")
                    : t("copy.dc76c143a8bb");
    // Unknown server prose may be in another language or include implementation detail.
    // Keep Error.message intact for diagnostics; expose only a bounded machine code in the UI.
    const safeCode = /^[a-z][a-z0-9_.-]{0,63}$/i.test(this.code)
      ? this.code
      : `http_${this.status || "unknown"}`;
    return appendSupportReference(
      `${guidance} ${t("refinement.locale.error.code", { code: safeCode })}`,
      this.requestId,
      this.status,
    );
  }
}

const SAFE_REQUEST_ID = /^[A-Za-z0-9._:-]{1,64}$/;

/** Correlation ids are reflected into visible copy and support reports. Keep the
 * same deliberately narrow alphabet as the server so a hostile proxy cannot
 * inject markup, control characters or personal prose through a header. */
function safeRequestId(value?: string | null): string | undefined {
  const candidate = value?.trim();
  return candidate && SAFE_REQUEST_ID.test(candidate) ? candidate : undefined;
}

function appendSupportReference(message: string, requestId: string | undefined, status: number) {
  return requestId && status >= 500
    ? `${message} ${t("activity.request", { id: requestId })}`
    : message;
}

function translateFieldErrors(
  fields?: Record<string, string[]>,
): Record<string, string[]> | undefined {
  if (!fields) return undefined;
  return Object.fromEntries(
    Object.entries(fields).map(([field, messages]) => [field, messages.map(translateFieldMessage)]),
  );
}

function translateFieldMessage(message: string): string {
  const exact: Record<string, string> = {
    "Bu alan zorunludur.": t("copy.5ad34b7b9e46"),
    "This field is required.": t("copy.5ad34b7b9e46"),
    "Enter a valid email address.": t("copy.5331105d3b00"),
    "This password is too common.": t("copy.1f29b340def0"),
    "This password is entirely numeric.": t("copy.ddf3cd86eb73"),
    "Invalid value.": t("copy.03ec540fd4cc"),
  };
  if (exact[message]) return exact[message];
  const minimum = message.match(/at least (\d+) characters/i);
  if (minimum?.[1]) return t("copy.45a69c97967f", { value1: minimum[1] });
  if (/too similar/i.test(message)) return t("copy.5800d22ccc2b");
  return t("refinement.locale.validation.invalidField");
}

function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

const UNSAFE = new Set(["POST", "PUT", "PATCH", "DELETE"]);

/** Notified whenever the server says the session is over, so the router can
 * send the person to sign-in without every call knowing about routing. */
type SessionListener = () => void;
const sessionListeners = new Set<SessionListener>();

export function onSessionExpired(listener: SessionListener) {
  sessionListeners.add(listener);
  return () => sessionListeners.delete(listener);
}

let csrfRequest: Promise<void> | null = null;

/** Fetch and verify the CSRF cookie before an unsafe browser request.
 *
 * A proxy can answer this path with a 404 HTML page or a 502 while the browser
 * still considers `fetch` successful.  Never mark the session as primed until
 * both the response and the readable cookie prove that the handshake worked.
 */
export async function primeCsrf(): Promise<void> {
  if (isNative()) return;
  if (readCookie("exognitos_csrftoken")) return;
  if (csrfRequest) return csrfRequest;

  csrfRequest = (async () => {
    let response: Response;
    try {
      response = await fetch(CSRF_PATH, { credentials: "same-origin" });
    } catch {
      throw new ApiError(0, "csrf_failed", "CSRF endpoint could not be reached.");
    }

    if (!response.ok) {
      let body: { code?: string; detail?: string } = {};
      try {
        body = (await response.json()) as typeof body;
      } catch {
        // A proxy-generated HTML response has no useful public detail.
      }
      throw new ApiError(
        response.status,
        body.code ?? "csrf_failed",
        body.detail ?? "CSRF endpoint did not accept the request.",
        undefined,
        response.headers.get("X-Request-ID"),
      );
    }
    if (!readCookie("exognitos_csrftoken")) {
      throw new ApiError(0, "csrf_failed", "CSRF cookie was not stored by the browser.");
    }
  })();

  try {
    await csrfRequest;
  } finally {
    csrfRequest = null;
  }
}

/** Request/response policy: CSRF on unsafe methods and one announcement when a
 * session ends. The authenticated web client deliberately does not send the
 * optional timezone-override header: the account preference is canonical, and
 * an implicit browser override would make the Settings choice ineffective. */
const auth: Middleware = {
  async onRequest({ request }) {
    if (!isNative() && UNSAFE.has(request.method)) {
      await primeCsrf();
      const token = readCookie("exognitos_csrftoken");
      if (token) request.headers.set("X-CSRFToken", token);
    }
    return request;
  },

  async onResponse({ response }) {
    if (response.status === 401) {
      for (const listener of sessionListeners) listener();
    }
    return response;
  },
};

export const api = createClient<paths>({
  baseUrl: API_BASE,
  credentials: isNative() ? "omit" : "same-origin",
  fetch: nativeFetch,
});

api.use(auth);

type FetchLike = { response: Response };

/** The success payload of an openapi-fetch result, with the error arm removed. */
type Unwrapped<R> = R extends { data?: infer D } ? NonNullable<D> : never;

/**
 * Unwrap an openapi-fetch result into data, or throw a typed error.
 *
 * Query and mutation functions call this so that TanStack Query sees a
 * rejected promise — its retry, error and boundary behaviour all depend on
 * failures being thrown rather than returned. The return type is inferred from
 * the contract, so call sites never restate a response shape.
 */
export async function unwrap<R extends FetchLike>(promise: Promise<R>): Promise<Unwrapped<R>> {
  let result: R;
  try {
    result = await promise;
  } catch {
    throw new ApiError(0, "network_error", "Sunucuya ulaşılamadı.");
  }

  const { data, error, response } = result as R & {
    data?: unknown;
    error?: unknown;
  };
  if (error !== undefined && error !== null) {
    const body = error as {
      code?: string;
      detail?: string;
      fields?: Record<string, string[]>;
      request_id?: string;
    };
    throw new ApiError(
      response.status,
      body.code ?? "error",
      body.detail ?? t("copy.643f2c5b4ff3"),
      body.fields,
      body.request_id ?? response.headers.get("X-Request-ID"),
    );
  }
  if (!response.ok) {
    throw new ApiError(
      response.status,
      "error",
      "İşlem tamamlanamadı.",
      undefined,
      response.headers.get("X-Request-ID"),
    );
  }
  return data as Unwrapped<R>;
}
